'use client';

/// <reference types="@types/google.maps" />

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

// Define minimal shapes to interop old/new Places objects without tight coupling
type LegacyPlaceResult = google.maps.places.PlaceResult;
type NewPlace = any; // google.maps.places.Place (new data model)
type AutocompleteSuggestionType = any; // google.maps.places.AutocompleteSuggestion
type AutocompleteSessionTokenType = any; // google.maps.places.AutocompleteSessionToken

interface AddressFormProps {
  onSubmit: ({ 
    streetName,
    houseNumber, 
    zipCode, 
    city, 
    propertyType,
    latitude,
    longitude
  }: { 
    streetName: string;
    houseNumber: string;
    zipCode: string; 
    city: string; 
    propertyType: 'apartment' | 'house';
    latitude: number;
    longitude: number;
  }) => void;
  dictionary: any;
  disableSubmit?: boolean;
  // Prefill support for session-resume
  initialStreetName?: string;
  initialHouseNumber?: string;
  initialZipCode?: string;
  initialCity?: string;
  initialLatitude?: number;
  initialLongitude?: number;
  initialPropertyType?: 'apartment' | 'house';
  autoOpen?: boolean;
  onOpen?: () => void;
}

export function AddressForm({ onSubmit, dictionary, disableSubmit = false, initialStreetName, initialHouseNumber, initialZipCode, initialCity, initialLatitude, initialLongitude, initialPropertyType, autoOpen = false, onOpen }: AddressFormProps) {
  const [propertyType, setPropertyType] = useState<'apartment' | 'house'>('apartment');
  const [showForm, setShowForm] = useState<boolean>(false);
  // Autocomplete (new Places predictions API)
  const [placesLibLoaded, setPlacesLibLoaded] = useState<boolean>(false);
  const placesLibRef = useRef<{ Place: any; AutocompleteSessionToken: any; AutocompleteSuggestion: any } | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<LegacyPlaceResult | NewPlace | null>(null);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestionType[]>([]);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [sessionToken, setSessionToken] = useState<AutocompleteSessionTokenType | null>(null);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const didOpenRef = useRef<boolean>(false);
  
  const [errors, setErrors] = useState<{
    address?: string; // Single error key for address input
    propertyType?: string; 
  }>({});

  // Initialize from prefilled values (session-resume)
  useEffect(() => {
    try {
      if (initialPropertyType) {
        setPropertyType(initialPropertyType);
      }
      const hasInitialAddress = Boolean(initialStreetName && initialHouseNumber && initialZipCode && initialCity && typeof initialLatitude === 'number' && typeof initialLongitude === 'number');
      if (autoOpen || hasInitialAddress) {
        setShowForm(true);
      }
      if (hasInitialAddress) {
        // Compose a minimal Place-like object so extractAddressComponents works
        const placeLike: any = {
          address_components: [
            { long_name: String(initialHouseNumber), types: ['street_number'] },
            { long_name: String(initialStreetName), types: ['route'] },
            { long_name: String(initialZipCode), types: ['postal_code'] },
            { long_name: String(initialCity), types: ['locality'] },
          ],
          geometry: {
            location: {
              lat: () => Number(initialLatitude),
              lng: () => Number(initialLongitude),
            }
          },
          formattedAddress: `${initialStreetName} ${initialHouseNumber}, ${initialZipCode} ${initialCity}`
        };
        setSelectedPlace(placeLike);
        setAddressInputValue(placeLike.formattedAddress);
        setErrors({});
      }
    } catch (_) {}
    // Run once on mount for initial props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load new Places library classes once available from Maps script
  useEffect(() => {
    let isMounted = true;
    const loadPlaces = async () => {
      try {
        // @ts-expect-error - importLibrary is available in modern Maps JS API
        const { Place, AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary('places');
        if (!isMounted) return;
        placesLibRef.current = { Place, AutocompleteSessionToken, AutocompleteSuggestion };
        setPlacesLibLoaded(true);
        setSessionToken(new AutocompleteSessionToken());
      } catch (err) {
        console.error('Failed to import Places library', err);
      }
    };
    // google may not be ready if MapProvider not yet loaded
    if (typeof window !== 'undefined' && (window as any).google?.maps?.importLibrary) {
      loadPlaces();
    }
    return () => { isMounted = false; };
  }, []);

  // Fire onOpen once when the address form becomes visible (either by button click or autoOpen)
  useEffect(() => {
    try {
      if (showForm && !didOpenRef.current) {
        didOpenRef.current = true;
        onOpen?.();
      }
    } catch (_) {}
  }, [showForm, onOpen]);

  // Function to extract address components from either legacy PlaceResult or new Place
  const extractAddressComponents = useCallback((place: any): { streetName: string, houseNumber: string, zipCode: string, city: string, latitude: number | null, longitude: number | null } => {
    let streetName = '';
    let houseNumber = '';
    let zipCode = '';
    let city = '';
    // Latitude/Longitude: support both legacy geometry.location and new place.location
    let latitude: number | null = null;
    let longitude: number | null = null;

    const maybeGeometry = (place as any).geometry;
    const maybeLocation = (place as any).location;
    if (maybeGeometry?.location) {
      const loc = maybeGeometry.location;
      if (typeof loc.lat === 'function') {
        latitude = loc.lat();
        longitude = loc.lng();
      } else if (typeof loc.lat === 'number') {
        latitude = loc.lat;
        longitude = loc.lng;
      }
    } else if (maybeLocation) {
      const loc = maybeLocation;
      if (typeof loc.lat === 'function') {
        latitude = loc.lat();
        longitude = loc.lng();
      } else if (typeof loc.lat === 'number') {
        latitude = loc.lat;
        longitude = loc.lng;
      }
    }

    // Address components: legacy (address_components with long_name) or new (addressComponents with longText)
    const legacyComponents: any[] | undefined = (place as any).address_components;
    const newComponents: any[] | undefined = (place as any).addressComponents;
    const components = legacyComponents ?? newComponents ?? [];

    components.forEach((component: any) => {
      const types: string[] = component?.types ?? [];
      // Value fallback between legacy and new
      const longVal: string = component?.long_name ?? component?.longText ?? '';
      if (types.includes('street_number')) {
        houseNumber = longVal;
      }
      if (types.includes('route')) {
        streetName = longVal;
      }
      if (types.includes('postal_code')) {
        zipCode = longVal;
      }
      if (types.includes('locality')) {
        city = longVal;
      } else if (types.includes('administrative_area_level_2') && !city) {
        city = longVal;
      } else if (types.includes('administrative_area_level_1') && !city) {
        city = longVal;
      }
    });

    return { streetName, houseNumber, zipCode, city, latitude, longitude };
  }, []);
  
  // --- Autocomplete Handlers (new API) ---
  const fetchPredictions = useCallback(async (query: string) => {
    if (!placesLibLoaded || !placesLibRef.current) return [] as AutocompleteSuggestionType[];
    if (!sessionToken && placesLibRef.current?.AutocompleteSessionToken) {
      setSessionToken(new placesLibRef.current.AutocompleteSessionToken());
    }
    const { AutocompleteSuggestion } = placesLibRef.current;
    const request: any = {
      input: query,
      // Restrict/bias within Belgium roughly
      locationRestriction: { west: 2.5, south: 49.5, east: 6.4, north: 51.5 },
      // Include routes so that users typing street-only still get results
      includedPrimaryTypes: ['street_address', 'premise', 'route'],
    };
    if (sessionToken) {
      request.sessionToken = sessionToken;
    }
    try {
      setIsFetchingSuggestions(true);
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      // Sort so that suggestions containing a digit are listed first, but do not hide the rest
      const list = (suggestions as AutocompleteSuggestionType[]).slice();
      list.sort((a: any, b: any) => {
        const aHasNum = /\d/.test(a?.placePrediction?.text?.toString?.() ?? '');
        const bHasNum = /\d/.test(b?.placePrediction?.text?.toString?.() ?? '');
        return Number(bHasNum) - Number(aHasNum);
      });
      return list;
    } catch (err) {
      console.error('fetchAutocompleteSuggestions error', err);
      return [] as AutocompleteSuggestionType[];
    } finally {
      setIsFetchingSuggestions(false);
    }
  }, [placesLibLoaded, sessionToken]);

  // Debounce input changes to fetch predictions
  useEffect(() => {
    if (!placesLibLoaded) return;
    if (!addressInputValue || addressInputValue.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(async () => {
      const s = await fetchPredictions(addressInputValue.trim());
      setSuggestions(s);
      setHighlightedIndex(s.length > 0 ? 0 : -1);
    }, 300);
    return () => clearTimeout(handle);
  }, [addressInputValue, placesLibLoaded, fetchPredictions]);

  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddressInputValue(e.target.value);
    // If user types manually, invalidate the selection
    if (selectedPlace) {
      setSelectedPlace(null);
      setErrors({ address: dictionary?.sellingForm?.selectFromList || 'Please select a valid address from the list.' });
    }
    // Clear specific address error if they start typing again
    if (errors.address) {
      setErrors(prev => ({ ...prev, address: undefined }));
    }
  };
  
  const handleInputFocus = () => {
    setIsInputFocused(true);
  };
  
  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    const container = inputContainerRef.current;
    if (container && next && container.contains(next)) {
      return;
    }
    // Close suggestions when focus leaves the input/suggestions container
    setIsInputFocused(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };
  
  const handleSelectSuggestion = useCallback(async (index: number) => {
    if (!placesLibLoaded || !placesLibRef.current) return;
    const suggestion = suggestions[index];
    if (!suggestion) return;
    try {
      let place: any = suggestion.placePrediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location'],
        sessionToken: sessionToken ?? undefined,
      });

      let { houseNumber, streetName, city } = extractAddressComponents(place);
      if (!houseNumber) {
        // Attempt a fallback by appending a default house number (1) to the street + city
        const baseText = streetName
          ? `${streetName} 1${city ? ', ' + city : ''}`
          : `${suggestion?.placePrediction?.text?.toString?.() ?? ''} 1`;
        const numberedSuggestions = await fetchPredictions(baseText);
        if (numberedSuggestions.length > 0) {
          const first = numberedSuggestions[0];
          const numberedPlace: any = first.placePrediction.toPlace();
          await numberedPlace.fetchFields({
            fields: ['addressComponents', 'formattedAddress', 'location'],
            sessionToken: sessionToken ?? undefined,
          });
          const extracted = extractAddressComponents(numberedPlace);
          if (extracted.houseNumber) {
            place = numberedPlace;
            houseNumber = extracted.houseNumber;
            streetName = extracted.streetName;
            city = extracted.city;
          }
        }
      }

      setSelectedPlace(place);
      const inputText = place?.formattedAddress ?? suggestion?.placePrediction?.text?.toString?.() ?? '';
      setAddressInputValue(inputText);
      setSuggestions([]);
      setHighlightedIndex(-1);
      setErrors({});
      // Reset token after a successful selection to close the session
      if (placesLibRef.current?.AutocompleteSessionToken) {
        setSessionToken(new placesLibRef.current.AutocompleteSessionToken());
      }
      // Explicitly exit focus state and blur the input to close dropdown
      setIsInputFocused(false);
      inputRef.current?.blur();
    } catch (err) {
      console.error('Failed to resolve place details', err);
      setErrors({ address: dictionary?.sellingForm?.invalidAddress || 'Please select a valid address from the list.' });
    }
  }, [suggestions, placesLibLoaded, dictionary, extractAddressComponents, sessionToken, fetchPredictions]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        void handleSelectSuggestion(highlightedIndex);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  };
  // --- End Autocomplete Handlers ---

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: typeof errors = {};

    // Validate that a place was selected
    if (!selectedPlace) {
      newErrors.address = dictionary?.sellingForm?.selectFromList || 'Please select a valid address from the list.';
      setErrors(newErrors);
      return;
    }
    
    // Extract data from the selected place *again* to be sure
    const { streetName, houseNumber, zipCode, city, latitude, longitude } = extractAddressComponents(selectedPlace);
    
    // Double check extraction results (especially lat/lng)
    if (!latitude || !longitude) {
      newErrors.address = dictionary?.sellingForm?.invalidAddress || 'Please select a valid address from the list.';
      setErrors(newErrors);
      return;
    }

    // Validate property type (selected via radio buttons below)
    const formData = new FormData(e.currentTarget);
    const propertyTypeValue = formData.get('propertyType') as 'apartment' | 'house'; // Get type from form data
    if (!propertyTypeValue) {
      newErrors.propertyType = dictionary?.sellingForm?.requiredField || 'Property type is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit({ 
      streetName,
      houseNumber, 
      zipCode, 
      city, 
      propertyType: propertyTypeValue, // Use value from form data
      latitude,
      longitude
    });
  };
  
  return (
    <>
      <div className="w-full">
        <p className="text-base md:text-sm text-zinc-800 pb-3">
          {dictionary?.sellingForm?.addressIntroBody ||
            'I\'ll ask a few quick questions so we can match your home with recent nearby comparables and compute a fair value. We use official data and AI to analyze your property and neighborhood.'}
        </p>
        {!showForm && (
          <div className="pt-1 pb-2">
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1 bg-white text-zinc-800 hover:text-zinc-950 font-semibold text-base md:text-sm focus:outline-none border border-zinc-800 px-3 py-1 rounded-md hover:bg-zinc-50 transition-colors"
            >
              {dictionary?.sellingForm?.addressIntroCTA || 'Start Step 1 — Address'}
              <ChevronDownIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="w-full">
          <p className="text-base md:text-sm text-zinc-800 pb-2 pt-3 border-t border-zinc-200">
            {/* Update instruction text */}
            {dictionary?.sellingForm?.addressAutocompleteMessage || 'Enter the address of the property:'}
          </p>
        
          {/* --- Autocomplete Input (new predictions API) --- */}
          <div className="mb-2 relative" ref={inputContainerRef} onBlur={handleContainerBlur}> 
            <input
              type="text"
              name="address" // Keep name for potential form data access if needed elsewhere
              placeholder={dictionary?.sellingForm?.addressPlaceholder || 'Start typing address...'}
              className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-800 focus:border-zinc-800 outline-none transition-shadow"
              value={addressInputValue}
              onChange={handleAddressInputChange}
              onKeyDown={handleInputKeyDown}
              onFocus={handleInputFocus}
              autoComplete="street-address"
              ref={inputRef}
            />
            {(isInputFocused && suggestions.length > 0) && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-zinc-300 rounded-lg shadow-sm overflow-hidden">
                <ul className="max-h-60 overflow-auto">
                  {suggestions.map((sugg, idx) => {
                    const text = sugg?.placePrediction?.text?.toString?.() ?? '';
                    return (
                      <li
                        key={idx}
                        className={`px-3 py-2 text-sm cursor-pointer ${idx === highlightedIndex ? 'bg-zinc-100' : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); }}
                        onClick={() => void handleSelectSuggestion(idx)}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                      >
                        {text}
                      </li>
                    );
                  })}
                </ul>
                <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-zinc-200">
                  <span className="text-[10px] text-zinc-500">Powered by</span>
                  <Image
                    src="https://maps.gstatic.com/mapfiles/api-3/images/google_gray.svg"
                    alt="Google"
                    width={12}
                    height={12}
                    className="h-3"
                    onMouseDown={(e) => e.preventDefault()}
                  />
                </div>
              </div>
            )}
            {/* Display Address Error */}
            {errors.address && <p className="text-red-600 text-sm mt-1">{errors.address}</p>}
          </div>
          {/* --- End Autocomplete Input --- */}

          {/* --- Conditional Rendering for Property Type & Submit --- */}
          {/* Show property type selection only after a valid address is selected */}
          {selectedPlace && (
            <>
              <div className="mt-2"> {/* Add some margin top */}
                <p className="text-base md:text-sm text-zinc-800 mb-2">
                  {dictionary?.sellingForm?.propertyTypeQuestion || 'What type of property is it?'}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="propertyTypeApartment" className="flex-1 flex items-center justify-center cursor-pointer p-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2">
                    <input
                      type="radio"
                      id="propertyTypeApartment"
                      name="propertyType" // Ensure name matches for FormData access
                      value="apartment"
                      checked={propertyType === 'apartment'}
                      onChange={() => setPropertyType('apartment')}
                      className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900 sr-only"
                    />
                    <span className="text-base md:text-sm text-zinc-800 font-medium">
                      {dictionary?.sellingForm?.propertyTypeApartment || 'Apartment'}
                    </span>
                  </label>

                  <label htmlFor="propertyTypeHouse" className="flex-1 flex items-center justify-center cursor-pointer p-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2">
                    <input
                      type="radio"
                      id="propertyTypeHouse"
                      name="propertyType" // Ensure name matches for FormData access
                      value="house"
                      checked={propertyType === 'house'}
                      onChange={() => setPropertyType('house')}
                      className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900 sr-only"
                    />
                    <span className="text-base md:text-sm text-zinc-800 font-medium">
                      {dictionary?.sellingForm?.propertyTypeHouse || 'House'}
                    </span>
                  </label>
                </div>
                {errors.propertyType && <p className="text-red-600 text-sm mt-1">{errors.propertyType}</p>}
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  // Disable submit if disabled or Places library not yet ready
                  disabled={disableSubmit || !placesLibLoaded} 
                  className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {dictionary?.sellingForm?.continueButton || 'Continue'}
                </button>
              </div>
            </>
          )}
          {/* --- End Conditional Rendering --- */}

        </form>
      )}

      {/* Removed old style tag as it's likely not needed */}
    </>
  );
}